import React from 'react';
import { Bug, AlertTriangle, ChevronRight, Globe, Server, Database } from 'lucide-react';
import { useNavigate } from 'react-router-dom';


interface RecentVulnerabilitiesProps {
  vulnerabilities: any[];
}

const RecentVulnerabilities: React.FC<RecentVulnerabilitiesProps> = ({ vulnerabilities }) => {
  const navigate = useNavigate();
  const getAssetIcon = (type: string) => {
    switch (type) {
      case 'web': return Globe;
      case 'server': return Server;
      default: return Globe;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-500 bg-red-500/10';
      case 'high': return 'text-orange-500 bg-orange-500/10';
      case 'medium': return 'text-yellow-500 bg-yellow-500/10';
      case 'low': return 'text-emerald-500 bg-emerald-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved': return 'text-blue-500 bg-blue-500/10';
      case 'duplicate': return 'text-purple-500 bg-purple-500/10';
      case 'open': return 'text-yellow-500 bg-yellow-500/10';
      case 'retest': return 'text-emerald-500 bg-emerald-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  return (
    <div className="bg-[#0F1629] rounded-xl p-6 relative overflow-hidden group">
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Bug className="h-5 w-5 text-red-500" />
            <h2 className="text-lg font-semibold text-white">Recent Vulnerabilities</h2>
          </div>
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-4 w-4 text-yellow-500 animate-pulse" />
          </div>
        </div>

        {/* Vulnerabilities List */}
        <div className="space-y-3 mb-4">
          {vulnerabilities.map((vuln) => {
            const AssetIcon = getAssetIcon(vuln.asset.type);
            return (
              <div
                key={vuln.id}
                className="bg-gray-800/20 rounded-lg p-4 hover:bg-gray-800/30 transition-all duration-200 group/item cursor-pointer"
                onClick={() => navigate(`/findings/${vuln.id}`)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(vuln.severity)}`}>
                        {vuln.severity.toUpperCase()}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(vuln.status)}`}>
                        {vuln.status.toUpperCase()}
                      </span>
                    </div>
                    <h3 className="text-sm font-medium text-white truncate mb-2">{vuln.title}</h3>
                    <div className="flex items-center space-x-2 text-xs text-gray-400">
                      <div className="flex items-center space-x-1">
                        <AssetIcon className="h-3 w-3" />
                        <span>{vuln.asset.name}</span>
                      </div>
                      <span>•</span>
                      <span>{vuln.discoveredAt}</span>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-500 opacity-0 group-hover/item:opacity-100 transition-opacity" />
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <button
          className="w-full bg-gray-800/30 hover:bg-gray-800/50 text-gray-400 hover:text-white py-2 rounded-lg transition-colors duration-200 text-sm font-medium flex items-center justify-center space-x-2 cursor-pointer"
          onClick={() => navigate('/findings')}
        >
          <span>View all vulnerabilities</span>
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default RecentVulnerabilities;